import { appActions } from 'app/pages/App/slice';
import {
  selectDialogNotiState,
  selectIsShowDialogChangePass,
  selectIsShowDialogForgotPass,
  selectIsShowDialogLogin,
  selectIsShowDialogRegister,
  selectLanguage,
} from 'app/pages/App/slice/selectors';
import { AuthDialogInfo, DialogNotiState } from 'app/pages/App/slice/types';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TYPE_COOKIE } from 'utils/constants';
import { setCookie } from 'utils/cookies';

export const useGlobalFunction = () => {
  const dispatch = useDispatch();
  const locale = useSelector(selectLanguage);
  const history = useHistory();
  const isShowDialogLogin = useSelector(selectIsShowDialogLogin);
  const isShowDialogRegister = useSelector(selectIsShowDialogRegister);
  const isShowDialogForgotPass = useSelector(selectIsShowDialogForgotPass);
  const isShowDialogChangePass = useSelector(selectIsShowDialogChangePass);
  const dialogNotiState = useSelector(selectDialogNotiState);

  const onLogout = useCallback(() => {
    dispatch(
      appActions.logout({
        callback: () => {
          history.push('/');
        },
      }),
    );
  }, [dispatch, history]);

  const onChangeLanguage = useCallback(
    (language: 'vi' | 'en') => {
      dispatch(appActions.onChangeLanguage(language));
      window.location.reload();
    },
    [dispatch],
  );

  const onOpenDialogLogin = useCallback(() => {
    dispatch(appActions.openDialogLogin());
  }, [dispatch]);

  const onOpenDialogRegister = useCallback(() => {
    dispatch(appActions.openDialogRegister());
  }, [dispatch]);

  const onCloseDialogAuth = useCallback(() => {
    dispatch(appActions.closeDialogAuth());
  }, [dispatch]);

  const onLoginSuccess = useCallback(
    async token => {
      await setCookie(TYPE_COOKIE.TOKEN, token);
      dispatch(appActions.changeIsLogged({ isLogged: true }));
      onCloseDialogAuth();
    },
    [dispatch, onCloseDialogAuth],
  );

  const onOpenDialogForgotPass = useCallback(
    (authDialogInfo?: AuthDialogInfo) => {
      dispatch(appActions.openDialogForgotPass(authDialogInfo ?? null));
    },
    [dispatch],
  );

  const onOpenDialogChangePass = useCallback(() => {
    dispatch(appActions.openDialogChangePass());
  }, [dispatch]);

  const onOpenDialogNoti = useCallback(
    (newState: DialogNotiState) => {
      dispatch(appActions.openDialogNoti(newState));
    },
    [dispatch],
  );

  const onCloseDialogNoti = useCallback(() => {
    dispatch(appActions.closeDialogNoti());
  }, [dispatch]);

  const onSetDisplayMaintenanceReminder = useCallback(
    (isDisplay: boolean) => {
      dispatch(appActions.setDisplayMaintenanceReminder(isDisplay));
    },
    [dispatch],
  );

  return useMemo(
    () => ({
      locale,
      onChangeLanguage,

      onLogout,
      onLoginSuccess,
      isShowDialogLogin,
      isShowDialogRegister,
      onOpenDialogLogin,
      onOpenDialogRegister,
      onCloseDialogAuth,
      isShowDialogForgotPass,
      isShowDialogChangePass,
      onOpenDialogForgotPass,
      onOpenDialogChangePass,
      onOpenDialogNoti,
      onCloseDialogNoti,
      dialogNotiState,
      onSetDisplayMaintenanceReminder,
    }),
    [
      locale,
      onChangeLanguage,
      onLogout,
      onLoginSuccess,
      isShowDialogLogin,
      isShowDialogRegister,
      onOpenDialogLogin,
      onOpenDialogRegister,
      onCloseDialogAuth,
      isShowDialogForgotPass,
      isShowDialogChangePass,
      onOpenDialogForgotPass,
      onOpenDialogChangePass,
      onOpenDialogNoti,
      onCloseDialogNoti,
      dialogNotiState,
      onSetDisplayMaintenanceReminder,
    ],
  );
};
